/**
 * usePermissions Hook
 * Provides permission checking utilities
 */
import { useContext, useMemo } from "react";
import { UserContext } from "../Context/UserContext";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../utils/authToken";
import { hasPermission, PERMISSIONS } from "../utils/permissions";

/**
 * Hook to check user permissions
 * @returns {Object} Permission checking utilities
 */
export const usePermissions = () => {
  const { Authorization } = useContext(UserContext);

  const userData = useMemo(() => {
    // Try to get token from context first, then from cookies
    const token = Authorization || (typeof window !== 'undefined' ? getToken() : null);
    
    if (!token) {
      return {
        permissions: [],
        accountType: null,
        tenantId: null,
        employeeId: null,
      };
    }

    try {
      const decoded = jwtDecode(token);
      return {
        permissions: decoded.permissions || [],
        accountType: decoded.accountType || null,
        tenantId: decoded.tenantId || decoded.user || null,
        employeeId: decoded.employeeId || null,
        userId: decoded.id || decoded.user || null,
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return {
        permissions: [],
        accountType: null,
        tenantId: null,
        employeeId: null,
      };
    }
  }, [Authorization]);

  /**
   * Check if user has a specific permission
   * @param {string|string[]} permission - Permission(s) to check
   * @returns {boolean}
   */
  const checkPermission = (permission) => {
    return hasPermission(userData.permissions, permission);
  };

  /**
   * Check if user is owner (has "*" permission)
   * @returns {boolean}
   */
  const isOwner = () => {
    return checkPermission(PERMISSIONS.OWNER);
  };

  /**
   * Check if user is employee
   * @returns {boolean}
   */
  const isEmployee = () => {
    return userData.accountType === "employee";
  };

  /**
   * Check if user is owner account
   * @returns {boolean}
   */
  const isOwnerAccount = () => {
    return userData.accountType === "owner";
  };

  return {
    permissions: userData.permissions,
    accountType: userData.accountType,
    tenantId: userData.tenantId,
    employeeId: userData.employeeId,
    userId: userData.userId,
    checkPermission,
    isOwner,
    isEmployee,
    isOwnerAccount,
    hasPermission: checkPermission, // Alias for consistency
  };
};

